import {
  cn,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
} from "@scribenote/brian-design-system";
import { useEffect, useMemo, useRef, type ReactNode } from "react";

import {
  actionUsageScope,
  eventPolicies,
  noPolicyText,
  policyJudgmentText,
  producesText,
  rootCandidateText,
  scopeVersionText,
  settledRequests,
  type CatalogCategory,
  type CatalogEntry,
  type CatalogIndex,
  CATEGORY_NOUN,
} from "./catalogGraph";
import { clinicTime, rate } from "./traceFormat";
import type {
  CatalogAction,
  CatalogCandidate,
  CatalogEvent,
  CatalogPolicy,
  CatalogScope,
} from "./types";

const Count = ({ value }: { value: number }) => (
  <span className={value === 0 ? "text-annotation" : undefined}>{value}</span>
);

/** "Never observed" means no usage in scope; `unseen` names a definition that was observed but never reached this outcome. */
export const ObservedAt = ({
  value,
  timezone,
  observed = false,
  unseen = "Never observed",
}: {
  value: string | null;
  timezone: string;
  observed?: boolean;
  unseen?: string;
}) =>
  value ? (
    <time dateTime={value}>{clinicTime(value, timezone)}</time>
  ) : (
    <span className="text-annotation">
      {observed ? unseen : "Never observed"}
    </span>
  );

const Rate = ({
  numerator,
  denominator,
}: {
  numerator: number;
  denominator: number;
}) => (
  <span
    className={cn("whitespace-nowrap", denominator === 0 && "text-annotation")}
  >
    {rate(numerator, denominator)}
  </span>
);

interface Context {
  index: CatalogIndex;
  candidates: CatalogCandidate[];
  scope: CatalogScope;
  timezone: string;
}

interface Column<T> {
  header: string;
  usage: boolean;
  align?: "right";
  /** Shown only when the scope includes every version. */
  allVersions?: true;
  render: (item: T, context: Context) => ReactNode;
}

const versionColumn = <T extends { version: string }>(): Column<T> => ({
  header: "Version",
  usage: false,
  allVersions: true,
  render: (item, { scope }) => (
    <span className="whitespace-nowrap">
      <span className="font-mono text-xs">{item.version}</span>
      {item.version === scope.current_policy_version && (
        <span className="text-xs text-annotation"> · current</span>
      )}
    </span>
  ),
});

const EVENT_COLUMNS: Column<CatalogEvent>[] = [
  {
    header: "Attached policy",
    usage: false,
    render: (item, { index, scope }) => {
      const policies = eventPolicies(item, index);
      if (policies.length === 0)
        return <span className="text-annotation">{noPolicyText(scope)}</span>;
      if (scope.policy_version !== null)
        return index.labels.get(policies[0].id)?.name;
      const note = [
        policies.length === 1 && policies[0].version,
        !policies.some(
          (policy) => policy.version === scope.current_policy_version,
        ) && "not in current",
      ].filter(Boolean);
      return (
        <>
          {policies.length} version{policies.length === 1 ? "" : "s"}
          {note.length > 0 && (
            <span className="block text-xs text-annotation">
              {note.join(" · ")}
            </span>
          )}
        </>
      );
    },
  },
  {
    header: "Root candidates",
    usage: false,
    align: "right",
    render: (item, { index }) => (
      <span className="whitespace-nowrap">
        {rootCandidateText(eventPolicies(item, index)) ?? (
          <span className="text-annotation">—</span>
        )}
      </span>
    ),
  },
  {
    header: "Times emitted",
    usage: true,
    align: "right",
    render: (item) => <Count value={item.usage.emitted} />,
  },
  {
    header: "Last emitted",
    usage: true,
    render: (item, { timezone }) => (
      <ObservedAt value={item.usage.last_emitted_at} timezone={timezone} />
    ),
  },
];

const POLICY_COLUMNS: Column<CatalogPolicy>[] = [
  versionColumn(),
  {
    header: "Candidates",
    usage: false,
    render: (item) =>
      `${item.root_candidate_count} root · ${item.nested_candidate_count} nested`,
  },
  {
    header: "Judgment",
    usage: false,
    render: (item, { candidates }) => policyJudgmentText(item, candidates),
  },
  {
    header: "Recorded decisions",
    usage: true,
    align: "right",
    render: (item) => <Count value={item.usage.decisions} />,
  },
  {
    header: "Last used",
    usage: true,
    render: (item, { timezone }) => (
      <ObservedAt value={item.usage.last_decided_at} timezone={timezone} />
    ),
  },
];

const CANDIDATE_COLUMNS: Column<CatalogCandidate>[] = [
  versionColumn(),
  {
    header: "Conditions",
    usage: false,
    align: "right",
    render: (item) => <Count value={item.conditions.length} />,
  },
  { header: "Produces", usage: false, render: (item) => producesText(item) },
  {
    header: "Considered",
    usage: true,
    align: "right",
    render: (item) => <Count value={item.usage.considered} />,
  },
  {
    header: "Chosen",
    usage: true,
    align: "right",
    render: (item) => (
      <Rate numerator={item.usage.chosen} denominator={item.usage.considered} />
    ),
  },
  {
    header: "Last chosen",
    usage: true,
    render: (item, { timezone }) => (
      <ObservedAt
        value={item.usage.last_chosen_at}
        timezone={timezone}
        observed={item.usage.considered > 0}
        unseen="Never chosen"
      />
    ),
  },
];

const ACTION_COLUMNS: Column<CatalogAction>[] = [
  {
    header: "Referring candidates",
    usage: false,
    align: "right",
    render: (item) => <Count value={item.referring_candidate_ids.length} />,
  },
  {
    header: "Requests",
    usage: true,
    align: "right",
    render: (item) => <Count value={item.usage.requests} />,
  },
  {
    header: "Applied",
    usage: true,
    align: "right",
    render: (item) => (
      <Rate
        numerator={item.usage.applied}
        denominator={settledRequests(item.usage)}
      />
    ),
  },
  {
    header: "Terminal failures",
    usage: true,
    align: "right",
    render: (item) =>
      item.usage.requests > 0 ? (
        <Count value={item.usage.failed} />
      ) : (
        <span className="text-annotation">—</span>
      ),
  },
  {
    header: "Last requested",
    usage: true,
    render: (item, { timezone }) => (
      <ObservedAt value={item.usage.last_requested_at} timezone={timezone} />
    ),
  },
];

const HEADERS: Record<
  CatalogCategory,
  readonly Pick<Column<never>, "header" | "usage" | "align" | "allVersions">[]
> = {
  events: EVENT_COLUMNS,
  policies: POLICY_COLUMNS,
  candidates: CANDIDATE_COLUMNS,
  actions: ACTION_COLUMNS,
};

const shown = <T extends Pick<Column<never>, "allVersions">>(
  columns: readonly T[],
  scope: CatalogScope,
): T[] =>
  columns.filter(
    (column) => !column.allVersions || scope.policy_version === null,
  );

const rowCells = (entry: CatalogEntry, context: Context): ReactNode[] => {
  const { scope } = context;
  switch (entry.category) {
    case "events":
      return shown(EVENT_COLUMNS, scope).map((column) =>
        column.render(entry.item, context),
      );
    case "policies":
      return shown(POLICY_COLUMNS, scope).map((column) =>
        column.render(entry.item, context),
      );
    case "candidates":
      return shown(CANDIDATE_COLUMNS, scope).map((column) =>
        column.render(entry.item, context),
      );
    case "actions":
      return shown(ACTION_COLUMNS, scope).map((column) =>
        column.render(entry.item, context),
      );
  }
};

const usageScope = (category: CatalogCategory, scope: CatalogScope) => {
  if (category === "events") return "all policy versions";
  if (category === "actions") return actionUsageScope(scope);
  return scope.policy_version === null
    ? "each row's version"
    : `policy ${scope.policy_version}`;
};

export const CatalogTable = ({
  category,
  entries,
  index,
  scope,
  anchor,
  onSelect,
}: {
  category: CatalogCategory;
  entries: CatalogEntry[];
  index: CatalogIndex;
  scope: CatalogScope;
  anchor: string | null;
  onSelect: (id: string) => void;
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const candidates = useMemo(
    () =>
      [...index.entries.values()].flatMap((entry) =>
        entry.category === "candidates" ? [entry.item] : [],
      ),
    [index],
  );
  const columns = [
    { header: CATEGORY_NOUN[category], usage: false, align: undefined },
    ...shown(HEADERS[category], scope),
  ];
  const definitionSpan = columns.filter((column) => !column.usage).length;
  const usageSpan = columns.length - definitionSpan;

  useEffect(() => {
    const container = tableRef.current?.parentElement;
    const row = anchor ? rowRefs.current.get(anchor) : undefined;
    const head = tableRef.current?.tHead;
    if (!container || !row) return;
    const bounds = container.getBoundingClientRect();
    const rowBounds = row.getBoundingClientRect();
    const top = bounds.top + (head?.offsetHeight ?? 0);
    if (rowBounds.top < top || rowBounds.bottom > bounds.bottom)
      container.scrollTop += rowBounds.top - top - 8;
  }, [anchor, category]);

  return (
    <LedgerTable
      ref={tableRef}
      aria-label={`${category} definitions and usage`}
      className="max-h-[28rem] overflow-y-auto"
    >
      <LedgerHead className="sticky top-0 z-10 bg-card">
        <tr>
          <LedgerHeadCell colSpan={definitionSpan} className="border-b-0 pb-0">
            Definition · {scopeVersionText(scope)}
          </LedgerHeadCell>
          <LedgerHeadCell
            colSpan={usageSpan}
            className="border-b-0 border-l border-l-rule pb-0"
          >
            Usage · {usageScope(category, scope)}
          </LedgerHeadCell>
        </tr>
        <tr>
          {columns.map((column, position) => (
            <LedgerHeadCell
              key={column.header}
              align={column.align}
              className={cn(
                "whitespace-nowrap",
                position === definitionSpan && "border-l border-l-rule",
              )}
            >
              {column.header}
            </LedgerHeadCell>
          ))}
        </tr>
      </LedgerHead>
      <LedgerBody>
        {entries.map((entry) => {
          const id = entry.item.id;
          const selected = id === anchor;
          const label = index.labels.get(id);
          return (
            <LedgerRow
              key={id}
              ref={(node) => {
                if (node) rowRefs.current.set(id, node);
                else rowRefs.current.delete(id);
              }}
              onClick={() => onSelect(id)}
              className={cn(
                "cursor-pointer hover:bg-smudge",
                selected && "bg-tint-accent even:bg-tint-accent",
              )}
            >
              <LedgerCell className="min-w-48 align-top">
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect(id);
                  }}
                  className="block w-full rounded-paper text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <span className="block break-words font-medium text-ink">
                    {label?.name}
                  </span>
                  {label?.context && (
                    <span className="mt-0.5 block break-words text-xs text-annotation">
                      {label.context}
                    </span>
                  )}
                </button>
              </LedgerCell>
              {rowCells(entry, {
                index,
                candidates,
                scope,
                timezone: scope.clinic_timezone,
              }).map((cell, position) => (
                <LedgerCell
                  key={columns[position + 1]?.header}
                  align={columns[position + 1]?.align}
                  className={cn(
                    "align-top tabular-nums",
                    position + 1 === definitionSpan && "border-l border-l-rule",
                  )}
                >
                  {cell}
                </LedgerCell>
              ))}
            </LedgerRow>
          );
        })}
      </LedgerBody>
    </LedgerTable>
  );
};
